import { useState, useEffect } from "react";

const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

export function useSocialFeed<T>(endpoint: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = `social_feed_${endpoint}`;

  const fetchFeed = async () => {
    setLoading(true);
    setError(null);

    // Check sessionStorage cache
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const entry: CacheEntry<T> = JSON.parse(cached);
        if (Date.now() - entry.timestamp < CACHE_TTL) {
          setData(entry.data);
          setLoading(false);
          return;
        }
      }
    } catch {
      // Ignore cache read errors
    }

    try {
      const res = await fetch(`/.netlify/functions/${endpoint}`);
      if (!res.ok) {
        throw new Error(`Failed to load feed (${res.status})`);
      }
      const json = await res.json();
      setData(json);

      // Cache the result
      try {
        const entry: CacheEntry<T> = { data: json, timestamp: Date.now() };
        sessionStorage.setItem(cacheKey, JSON.stringify(entry));
      } catch {
        // Ignore cache write errors
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load feed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeed();
  }, [endpoint]);

  return { data, loading, error, retry: fetchFeed };
}
